// Catalog service. One normalized shape (CatalogResult) and one search entry
// point (searchCatalog) over a vetted source hierarchy. Today the only
// connected adapters read the curated seed JSON; a live vendor adapter (an
// affiliate feed, a distributor CSV) implements the same CatalogSource
// contract and registers itself, with zero changes to callers or the UI.
//
// searchCatalog federates across the sources for a category, ranks by trust
// tier first (so Benjamin Moore outranks a tier-2 brand on an equal match)
// then by relevance, and returns the top N with full attribution.

import paintData from "../../data/seed/paints.json" with { type: "json" };
import floorData from "../../data/seed/flooring.json" with { type: "json" };
import { sourceById, type Category } from "../data/sources.js";

export interface CatalogResult {
  sourceId: string;
  sourceName: string;
  tier: number;
  category: Category;
  id: string; // code / sku
  name: string;
  hex?: string;
  lrv?: number;
  temperature?: number; // so a pulled-in pick still feeds insight + coherence
  chroma?: number;
  undertone?: string;
  sub?: string; // sub-category, e.g. flooring "lvp" / "tile"
  price?: number;
  detail: string; // one-line human-facing facts
}

// A source that can answer a text query in its category. Curated adapters
// below implement it over seed JSON; a live vendor adapter would fetch.
export interface CatalogSource {
  id: string;
  category: Category;
  search(query: string): CatalogResult[];
}

const registry: CatalogSource[] = [];
export function registerSource(s: CatalogSource): void {
  registry.push(s);
}

function tierOf(sourceId: string): number {
  return sourceById(sourceId)?.tier ?? 9;
}
function nameOf(sourceId: string): string {
  return sourceById(sourceId)?.name ?? sourceId;
}

function relevance(text: string, q: string): number {
  const t = text.toLowerCase();
  if (!q) return 0.1;
  if (t.startsWith(q)) return 1;
  if (t.includes(q)) return 0.6;
  return 0;
}

// ---- Curated paint adapters (one per connected brand) ----------------------

interface SeedColor {
  brand: string; code: string; name: string; hex: string; lrv: number;
  temperature: number; undertone_family: string; chroma_opinion: number;
}
const COLORS = (paintData as { colors: SeedColor[] }).colors;

const BRAND_SOURCE: Record<string, string> = {
  "Benjamin Moore": "benjamin-moore",
  "Sherwin-Williams": "sherwin-williams",
  "Behr": "behr",
};

function makePaintSource(brand: string, sourceId: string): CatalogSource {
  return {
    id: sourceId,
    category: "paint",
    search(query: string): CatalogResult[] {
      const q = query.trim().toLowerCase();
      return COLORS.filter((c) => c.brand === brand)
        .map((c) => ({ c, r: Math.max(relevance(c.name, q), relevance(c.code, q), relevance(c.undertone_family, q)) }))
        .filter((x) => x.r > 0)
        .sort((a, b) => b.r - a.r)
        .map(({ c }) => ({
          sourceId, sourceName: nameOf(sourceId), tier: tierOf(sourceId), category: "paint" as Category,
          id: c.code, name: c.name, hex: c.hex, lrv: c.lrv,
          temperature: c.temperature, chroma: c.chroma_opinion, undertone: c.undertone_family,
          detail: `${c.code} · ${c.undertone_family} · LRV ${c.lrv}`,
        }));
    },
  };
}

// ---- Curated flooring adapter ----------------------------------------------

interface SeedFloor {
  category: string; name: string; stain: string; hex: string; lrv: number;
  temperature: number; undertone: string; chroma_opinion: number;
  texture: string; price_per_sqft: number;
}
const FLOORS = (floorData as { floors: SeedFloor[] }).floors;

const flooringSource: CatalogSource = {
  id: "curated-flooring",
  category: "flooring",
  search(query: string): CatalogResult[] {
    const q = query.trim().toLowerCase();
    return FLOORS.map((f) => ({ f, r: Math.max(relevance(f.name, q), relevance(f.stain, q), relevance(f.category, q), relevance(f.undertone, q)) }))
      .filter((x) => x.r > 0)
      .sort((a, b) => b.r - a.r)
      .map(({ f }) => ({
        sourceId: "curated-flooring", sourceName: nameOf("curated-flooring"), tier: tierOf("curated-flooring"), category: "flooring" as Category,
        id: f.name, name: f.name, hex: f.hex, lrv: f.lrv,
        temperature: f.temperature, chroma: f.chroma_opinion, undertone: f.undertone,
        sub: f.category, price: f.price_per_sqft,
        detail: `${f.category} · ${f.stain} · $${f.price_per_sqft.toFixed(2)}/sqft`,
      }));
  },
};

// Register the connected adapters.
for (const [brand, id] of Object.entries(BRAND_SOURCE)) registerSource(makePaintSource(brand, id));
registerSource(flooringSource);

// ---- Federated search ------------------------------------------------------

export function searchCatalog(query: string, category: Category, limit = 6): CatalogResult[] {
  const hits = registry
    .filter((s) => s.category === category)
    .flatMap((s) => s.search(query));
  // Trust tier first, then relevance (encoded by each adapter's own sort,
  // approximated here by startsWith on the name), then LRV for stability.
  const q = query.trim().toLowerCase();
  return hits
    .sort((a, b) =>
      a.tier - b.tier ||
      relevance(b.name, q) - relevance(a.name, q) ||
      (b.lrv ?? 0) - (a.lrv ?? 0))
    .slice(0, limit);
}
